import { v4 as uuidv4 } from "uuid";
import type { ResourceData, ResourceField } from "@/types";
import {
  Package,
  ShoppingCart,
  PieChart,
  UserRound,
  BriefcaseBusiness,
  Truck,
  Building2,
  Lock as LockIcon,
  MapPin,
  Tag,
  Wrench,
  Briefcase,
  Building,
  List,
  Puzzle,
  Layers,
  FileText,
  History,
  Tag as TagIcon,
  Car,
  Users,
  Building as BuildingIcon,
  Shield,
  Power,
} from "lucide-react";

export interface GroupItem {
  title: string;
  url: string;
  requiresPermission?: boolean;
}

export interface ResourceGroup {
  id: string;
  name: string;
  icon: React.ComponentType;
  description: string;
  defaultUrl: string;
  subItems?: GroupItem[];
}

export interface ResourceMetadata {
  name: string;
  url: string;
  icon?: React.ComponentType;
  description: string;
  fields: ResourceField[];
  navigation?: {
    section: "role" | "management";
    group?: keyof typeof groups;
  };
}

// Define resource groups
export const groups: Record<string, ResourceGroup> = {
  products: {
    id: uuidv4(),
    name: "Products",
    icon: Package,
    description: "Product management",
    defaultUrl: "/dashboard/products",
    subItems: [
      {
        title: "All Products",
        url: "/dashboard/products",
        requiresPermission: true,
      },
      {
        title: "Parents",
        url: "/dashboard/products/parents",
        requiresPermission: true,
      },
      {
        title: "Product Groups",
        url: "/dashboard/products/groups",
        requiresPermission: true,
      },
      {
        title: "Brands",
        url: "/dashboard/products/brands",
        requiresPermission: true,
      },
      // Temporarily hidden - keeping for permissions functionality
      // {
      //   title: "Boxes",
      //   url: "/dashboard/products/boxes",
      //   requiresPermission: true,
      // },
      // {
      //   title: "Materials",
      //   url: "/dashboard/products/materials",
      //   requiresPermission: true,
      // },
      // {
      //   title: "Components",
      //   url: "/dashboard/products/components",
      //   requiresPermission: true,
      // },
      {
        title: "Pricing Tiers",
        url: "/dashboard/products/pricing-tiers",
        requiresPermission: true,
      },
      {
        title: "Featured",
        url: "/dashboard/featured",
        requiresPermission: true,
      },
      {
        title: "Attributes",
        url: "/dashboard/products/attributes",
        requiresPermission: true,
      },
    ],
  },
  jobs: {
    id: uuidv4(),
    name: "Jobs",
    icon: BriefcaseBusiness,
    description: "Job management",
    defaultUrl: "/dashboard/jobs",
    subItems: [
      {
        title: "All Jobs",
        url: "/dashboard/jobs",
        requiresPermission: true,
      },
      {
        title: "Job Tasks",
        url: "/dashboard/jobs/tasks",
        requiresPermission: true,
      },
      {
        title: "Departments",
        url: "/dashboard/jobs/departments",
        requiresPermission: true,
      },
    ],
  },
  vehicles: {
    id: uuidv4(),
    name: "Vehicles",
    icon: Truck,
    description: "Vehicle management",
    defaultUrl: "/dashboard/vehicles",
    subItems: [
      {
        title: "Vehicle Brands",
        url: "/dashboard/vehicles/brands",
        requiresPermission: true,
      },
      {
        title: "Vehicle Models",
        url: "/dashboard/vehicles/models",
        requiresPermission: true,
      },
      {
        title: "Vehicle Groups",
        url: "/dashboard/vehicles/groups",
        requiresPermission: true,
      },
      {
        title: "Vehicle Types",
        url: "/dashboard/vehicles/types",
        requiresPermission: true,
      },
      {
        title: "Company Fleets",
        url: "/dashboard/vehicles/company-fleets",
        requiresPermission: true,
      },
      {
        title: "User Fleets",
        url: "/dashboard/vehicles/user-fleets",
        requiresPermission: true,
      },
      {
        title: "Company Vehicles",
        url: "/dashboard/vehicles/company-vehicles",
        requiresPermission: true,
      },
      {
        title: "User Vehicles",
        url: "/dashboard/vehicles/user-vehicles",
        requiresPermission: true,
      },
    ],
  },
  business: {
    id: uuidv4(),
    name: "Business",
    icon: Building2,
    description: "Business management",
    defaultUrl: "/dashboard/business",
    subItems: [
      {
        title: "Companies",
        url: "/dashboard/companies",
        requiresPermission: true,
      },
      {
        title: "Equipment",
        url: "/dashboard/equipment",
        requiresPermission: true,
      },
      // Temporarily hidden - keeping for permissions functionality
      // {
      //   title: "Suppliers",
      //   url: "/dashboard/suppliers",
      //   requiresPermission: true,
      // },
    ],
  },
  analytics: {
    id: uuidv4(),
    name: "Analytics",
    icon: PieChart,
    description: "Analytics management",
    defaultUrl: "/dashboard/analytics",
    subItems: [
      {
        title: "All Analytics",
        url: "/dashboard/analytics",
      },
    ],
  },
  orders: {
    id: uuidv4(),
    name: "Orders",
    icon: ShoppingCart,
    description: "Order management",
    defaultUrl: "/dashboard/orders",
    subItems: [
      {
        title: "All Orders",
        url: "/dashboard/orders",
        requiresPermission: true,
      },
      {
        title: "Carts",
        url: "/dashboard/carts",
        requiresPermission: true,
      },
    ],
  },
  users: {
    id: uuidv4(),
    name: "Users",
    icon: UserRound,
    description: "User management",
    defaultUrl: "/dashboard/users",
    subItems: [
      {
        title: "All Users",
        url: "/dashboard/users",
        requiresPermission: true,
      },
      {
        title: "Permissions",
        url: "/dashboard/permissions",
        requiresPermission: true,
      },
    ],
  },
  protection: {
    id: uuidv4(),
    name: "Protection",
    icon: Shield,
    description: "Site protection and security management",
    defaultUrl: "/dashboard/protection/kill-switch",
    subItems: [
      {
        title: "Kill Switch",
        url: "/dashboard/protection/kill-switch",
        requiresPermission: true,
      },
      {
        title: "Banned Users",
        url: "/dashboard/users/banned",
        requiresPermission: true,
      },
    ],
  },
};

const resourcesConfig = {
  users: {
    name: "Users",
    url: "/dashboard/users",
    icon: UserRound,
    description: "User management",
    navigation: {
      section: "management",
      group: "users",
    },
    fields: [
      { name: "firstName", description: "First name" },
      { name: "lastName", description: "Last name" },
      { name: "email", description: "Email address" },
      { name: "phone", description: "Phone number" },
      { name: "image", description: "Profile image" },
      { name: "role", description: "User role" },
      { name: "companyID", description: "Associated company" },
      { name: "emailVerified", description: "Email verification status" },
      { name: "dateOfBirth", description: "Date of birth" },
      { name: "isActive", description: "Active status" },
      { name: "lastLogin", description: "Last login date" },
    ],
  },
  "address-book": {
    name: "Address Book",
    url: "/dashboard/address-book",
    icon: MapPin,
    description: "Address book management",
    navigation: {
      section: "management",
    },
    fields: [
      { name: "name", description: "Address name" },
      { name: "address1", description: "Address line 1" },
      { name: "address2", description: "Address line 2" },
      { name: "city", description: "City" },
      { name: "state", description: "State" },
      { name: "postalCode", description: "Postal code" },
      { name: "country", description: "Country" },
      { name: "coordinates", description: "GPS coordinates" },
      { name: "buildingImage", description: "Building image" },
    ],
  },
  brands: {
    name: "Brands",
    url: "/dashboard/products/brands",
    icon: Package,
    description: "Brand management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Brand name" },
      { name: "description", description: "Brand description" },
      { name: "website", description: "Brand website" },
      { name: "logo", description: "Brand logo" },
      { name: "status", description: "Brand status" },
    ],
  },
  // TEMPORARILY HIDDEN: Navigation removed but permissions still functional
  boxes: {
    name: "Boxes",
    url: "/dashboard/products/boxes",
    icon: Package,
    description: "Box management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Box name" },
      { name: "description", description: "Box description" },
      { name: "flat_width", description: "Flat width (cm)" },
      { name: "flat_height", description: "Flat height (cm)" },
      { name: "flat_length", description: "Flat length (cm)" },
      { name: "assembled_width", description: "Assembled width (cm)" },
      { name: "assembled_height", description: "Assembled height (cm)" },
      { name: "assembled_length", description: "Assembled length (cm)" },
      { name: "weight", description: "Weight (kg)" },
      { name: "material_id", description: "Material" },
      { name: "images", description: "Box images" },
      { name: "design_files", description: "Design files" },
      { name: "status", description: "Box status" },
    ],
  },
  // TEMPORARILY HIDDEN: Navigation removed but permissions still functional
  materials: {
    name: "Materials",
    url: "/dashboard/products/materials",
    icon: Package,
    description: "Material management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Material name" },
      { name: "description", description: "Material description" },
      { name: "type", description: "Material type" },
      { name: "color", description: "Material color (hex code)" },
      { name: "finish", description: "Material finish" },
      { name: "ref", description: "Reference code (Y, R, W, etc.)" },
      {
        name: "material_code",
        description: "Material code (4083, 4088, etc.)",
      },
      { name: "brand", description: "Brand (3M, Avery, etc.)" },
      { name: "class", description: "Class (1, 2, etc.)" },
      { name: "purchase_width", description: "Purchase width (cm)" },
      { name: "purchase_height", description: "Purchase height (cm)" },
      { name: "purchase_length", description: "Purchase length (cm)" },
      { name: "purchase_weight", description: "Purchase weight (kg)" },
      { name: "purchase_price", description: "Purchase price ($)" },
      { name: "recyclable", description: "Recyclable status" },
      { name: "images", description: "Material images" },
      { name: "status", description: "Material status" },
    ],
  },
  // TEMPORARILY HIDDEN: Navigation removed but permissions still functional
  components: {
    name: "Components",
    url: "/dashboard/products/components",
    icon: Layers,
    description: "Component management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Component name" },
      { name: "description", description: "Component description" },
      { name: "component_code", description: "Component code" },
      {
        name: "type",
        description: "Component type (manufactured/purchased/hybrid)",
      },
      { name: "cost_price", description: "Cost price ($)" },
      { name: "sell_price", description: "Sell price ($)" },
      { name: "material_id", description: "Material" },
      { name: "equipment_id", description: "Equipment" },
      { name: "job_task_id", description: "Job task" },
      { name: "manufactured_by", description: "Manufactured by" },
      { name: "coating_required", description: "Coating required" },
      { name: "coating_supplier_id", description: "Coating supplier" },
      { name: "coating_cost", description: "Coating cost ($)" },
      { name: "coating_lead_time", description: "Coating lead time" },
      { name: "supplier_id", description: "Supplier" },
      { name: "supplier_part_number", description: "Supplier part number" },
      { name: "lead_time", description: "Lead time" },
      { name: "minimum_order_quantity", description: "Minimum order quantity" },
      { name: "weight", description: "Weight (kg)" },
      { name: "width", description: "Width (cm)" },
      { name: "height", description: "Height (cm)" },
      { name: "length", description: "Length (cm)" },
      { name: "images", description: "Component images" },
      { name: "technical_drawings", description: "Technical drawings" },
      { name: "notes", description: "Notes" },
      { name: "status", description: "Component status" },
    ],
  },
  permissions: {
    name: "Permissions",
    url: "/dashboard/permissions",
    icon: LockIcon,
    description: "Permission management",
    navigation: {
      section: "management",
      group: "users",
    },
    fields: [
      { name: "resource", description: "Resource" },
      { name: "action", description: "Action" },
      { name: "canCreate", description: "Create permission" },
      { name: "canRead", description: "Read permission" },
      { name: "canUpdate", description: "Update permission" },
      { name: "canDelete", description: "Delete permission" },
      { name: "fieldPermissions", description: "Field-level permissions" },
    ],
  },
  companies: {
    name: "Companies",
    url: "/dashboard/companies",
    icon: Building2,
    description: "Company management",
    navigation: {
      section: "role",
      group: "business",
    },
    fields: [
      { name: "name", description: "Company name" },
      { name: "cell", description: "Phone number" },
      { name: "email", description: "Email address" },
      { name: "website", description: "Company website" },
      { name: "businessNumber", description: "Business number" },
      { name: "logo", description: "Company logo" },
    ],
  },
  "pricing-tiers": {
    name: "Pricing Tiers",
    url: "/dashboard/products/pricing-tiers",
    icon: Package,
    description: "Pricing tier management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Tier name" },
      { name: "description", description: "Tier description" },
      { name: "isDefault", description: "Default tier status" },
    ],
  },
  "vehicle-types": {
    name: "Vehicle Types",
    url: "/dashboard/vehicles/types",
    icon: Truck,
    description: "Vehicle type management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Vehicle type name" },
      { name: "description", description: "Vehicle type description" },
      { name: "image", description: "Vehicle type image" },
    ],
  },
  "vehicle-groups": {
    name: "Vehicle Groups",
    url: "/dashboard/vehicles/groups",
    icon: Truck,
    description: "Vehicle group management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Group name" },
      { name: "description", description: "Group description" },
      { name: "slug", description: "URL-friendly identifier" },
      { name: "heroImage", description: "Featured image" },
      { name: "images", description: "Additional images" },
    ],
  },
  equipment: {
    name: "Equipment",
    url: "/dashboard/equipment",
    icon: Wrench,
    description: "Equipment management",
    navigation: {
      section: "role",
      group: "business",
    },
    fields: [
      { name: "name", description: "Equipment name" },
      { name: "serialNumber", description: "Serial number" },
      { name: "manufacturer", description: "Manufacturer" },
      { name: "year", description: "Year" },
      { name: "purchaseDate", description: "Purchase date" },
      { name: "purchasePrice", description: "Purchase price" },
      { name: "location", description: "Location" },
      { name: "status", description: "Status" },
      { name: "lastInspection", description: "Last inspection date" },
      { name: "nextInspection", description: "Next inspection date" },
      { name: "warrantyStatus", description: "Warranty status" },
    ],
  },
  // TEMPORARILY HIDDEN: Navigation removed but permissions still functional
  suppliers: {
    name: "Suppliers",
    url: "/dashboard/suppliers",
    icon: Truck,
    description: "Supplier management",
    navigation: {
      section: "role",
      group: "business",
    },
    fields: [
      { name: "name", description: "Supplier name" },
      { name: "company_id", description: "Company" },
      { name: "type", description: "Supplier type" },
      { name: "status", description: "Supplier status" },
      { name: "contact_person", description: "Contact person" },
      { name: "email", description: "Email address" },
      { name: "phone", description: "Phone number" },
      { name: "website", description: "Website" },
      { name: "payment_terms", description: "Payment terms" },
      { name: "shipping_terms", description: "Shipping terms" },
      { name: "account_number", description: "Account number" },
      { name: "tax_id", description: "Tax ID" },
      { name: "quality_rating", description: "Quality rating (1-5)" },
      { name: "delivery_rating", description: "Delivery rating (1-5)" },
      { name: "price_rating", description: "Price rating (1-5)" },
      { name: "is_preferred", description: "Preferred supplier" },
      { name: "is_approved", description: "Approved supplier" },
      { name: "minimum_order_amount", description: "Minimum order amount ($)" },
      { name: "currency", description: "Currency" },
      { name: "component_ids", description: "Components" },
      { name: "product_ids", description: "Products" },
      { name: "notes", description: "Notes" },
    ],
  },
  analytics: {
    name: "Analytics",
    url: "/dashboard/analytics",
    icon: PieChart,
    description: "Analytics management",
    navigation: {
      section: "role",
      group: "analytics",
    },
    fields: [],
  },
  orders: {
    name: "Orders",
    url: "/dashboard/orders",
    icon: ShoppingCart,
    description: "Order management",
    navigation: {
      section: "role",
      group: "orders",
    },
    fields: [
      { name: "orderNumber", description: "Order number" },
      { name: "status", description: "Order status" },
      { name: "paymentStatus", description: "Payment status" },
      { name: "total", description: "Total amount" },
      { name: "shippingMethod", description: "Shipping method" },
      { name: "trackingNumber", description: "Tracking number" },
      { name: "customerNotes", description: "Customer notes" },
      { name: "internalNotes", description: "Internal notes" },
      { name: "engineeringNotes", description: "Engineering notes" },
      { name: "qualityNotes", description: "Quality notes" },
      { name: "productionNotes", description: "Production notes" },
      {
        name: "estimatedCompletionDate",
        description: "Estimated completion date",
      },
      { name: "priority", description: "Priority status" },
    ],
  },
  carts: {
    name: "Carts",
    url: "/dashboard/carts",
    icon: FileText,
    description: "Cart management",
    navigation: {
      section: "role",
      group: "orders",
    },
    fields: [
      { name: "quote_number", description: "Quote number" },
      { name: "total", description: "Total amount" },
      { name: "items", description: "Quote items" },
      { name: "notes", description: "Quote notes" },
      { name: "created", description: "Created date" },
      { name: "updated", description: "Last updated date" },
    ],
  },
  products: {
    name: "Products",
    url: "/dashboard/products",
    icon: Package,
    description: "Product management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      // Basic Information
      { name: "title", description: "Product title" },
      { name: "part_number", description: "Part number" },
      { name: "description", description: "Product description" },
      { name: "features", description: "Product features list" },
      { name: "status", description: "Product status (draft/active)" },

      // Brand and Categorization
      { name: "brand_id", description: "Product brand ID" },
      { name: "product_brand", description: "Product brand information" },
      { name: "product_groups", description: "Associated product groups" },
      { name: "tags", description: "Product tags for categorization" },

      // Media and Files
      { name: "images", description: "Product images" },
      { name: "hover_image", description: "Image shown on hover" },
      { name: "pdf_attachments", description: "PDF documentation files" },
      { name: "design_files", description: "Design and artwork files" },

      // Pricing and Inventory
      { name: "cost_price", description: "Cost price (sensitive)" },
      { name: "tax_rate", description: "Tax rate percentage" },
      { name: "moq", description: "Minimum order quantity" },
      {
        name: "product_pricing_tiers",
        description: "Pricing tier configurations (sensitive)",
      },

      // Physical Specifications
      { name: "weight", description: "Product weight (kg)" },
      { name: "width", description: "Product width (mm)" },
      { name: "height", description: "Product height (mm)" },
      { name: "length", description: "Product length (mm)" },
      { name: "shipping_width", description: "Shipping width (mm)" },
      { name: "shipping_height", description: "Shipping height (mm)" },
      { name: "shipping_length", description: "Shipping length (mm)" },
      { name: "shipping_weight", description: "Shipping weight (kg)" },

      // SEO and Marketing
      { name: "meta_title", description: "SEO meta title" },
      { name: "meta_description", description: "SEO meta description" },

      // Editor and Customization
      { name: "editor", description: "Editor type (standard/custom)" },
      { name: "custom", description: "Custom editor configuration" },
      {
        name: "rock_board",
        description: "Rock board electrical specifications",
      },

      // Relationships and Variants
      { name: "parent", description: "Parent product relationship" },
      {
        name: "attribute_values",
        description: "Product attributes and variants",
      },
      { name: "vehicle_types", description: "Compatible vehicle types" },

      // Product Relationships
      { name: "attachments", description: "Related product attachments" },
      {
        name: "cross_sells",
        description: "Cross-sell product recommendations",
      },
      { name: "upsells", description: "Upsell product recommendations" },

      // Business Rules
      {
        name: "exclusive_companies",
        description: "Companies with exclusive access (sensitive)",
      },
      {
        name: "company_part_numbers",
        description: "Company-specific part numbers",
      },
    ],
  },
  "job-departments": {
    name: "Job Departments",
    url: "/dashboard/jobs/departments",
    icon: Building,
    description: "Job department management",
    navigation: {
      section: "role",
      group: "jobs",
    },
    fields: [
      { name: "name", description: "Department name" },
      { name: "code", description: "Department code" },
      { name: "description", description: "Department description" },
      { name: "parentDepartmentId", description: "Parent department" },
    ],
  },
  jobs: {
    name: "Jobs",
    url: "/dashboard/jobs",
    icon: Briefcase,
    description: "Job management",
    navigation: {
      section: "role",
      group: "jobs",
    },
    fields: [
      { name: "title", description: "Job title" },
      { name: "description", description: "Job description" },
      { name: "employmentType", description: "Employment type" },
      { name: "departmentId", description: "Department" },
      { name: "reportsTo", description: "Reports to" },
    ],
  },
  "job-tasks": {
    name: "Job Tasks",
    url: "/dashboard/jobs/tasks",
    icon: BriefcaseBusiness,
    description: "Job task management",
    navigation: {
      section: "role",
      group: "jobs",
    },
    fields: [
      { name: "title", description: "Task title" },
      { name: "description", description: "Task description" },
      { name: "instructions", description: "Task instructions" },
      { name: "estimatedDuration", description: "Estimated duration" },
      { name: "equipmentId", description: "Required equipment" },
    ],
  },
  tags: {
    name: "Tags",
    url: "/dashboard/tags",
    icon: Tag,
    description: "Tag management",
    navigation: {
      section: "management",
    },
    fields: [{ name: "name", description: "Tag name" }],
  },
  "product-groups": {
    name: "Product Groups",
    url: "/dashboard/products/groups",
    icon: Package,
    description: "Product group management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "name", description: "Group name" },
      { name: "description", description: "Group description" },
      { name: "slug", description: "URL-friendly identifier" },
      { name: "hero_image", description: "Featured image" },
      { name: "images", description: "Additional images" },
      { name: "product_ids", description: "Associated products" },
    ],
  },
  attributes: {
    name: "Attributes",
    url: "/dashboard/products/attributes",
    icon: List,
    description: "Product attribute management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [{ name: "name", description: "Attribute name" }],
  },
  builder: {
    name: "Builder",
    url: "/dashboard/builder",
    icon: Puzzle,
    description: "Design builder management",
    navigation: {
      section: "role",
    },
    fields: [
      { name: "name", description: "Design name" },
      { name: "description", description: "Design description" },
      { name: "blocks", description: "Design blocks configuration" },
      { name: "thumbnail", description: "Design thumbnail" },
      { name: "boundWidth", description: "Design bound width" },
      { name: "boundHeight", description: "Design bound height" },
    ],
  },
  "product-parents": {
    name: "Parents",
    url: "/dashboard/products/parents",
    icon: Layers,
    description: "Product parent management",
    navigation: {
      section: "role",
      group: "products",
    },
    fields: [
      { name: "parent_name", description: "Parent name" },
      { name: "parent_image", description: "Parent image" },
      { name: "product_ids", description: "Associated product variants" },
    ],
  },
  freight: {
    name: "Freight Companies",
    url: "/dashboard/freight",
    icon: Truck,
    description: "Freight company management",
    navigation: {
      section: "management",
    },
    fields: [{ name: "company_name", description: "Freight company name" }],
  },
  "audit-logs": {
    name: "Audit Logs",
    url: "/dashboard/audit-logs",
    icon: History,
    description: "System audit logs for staff and admin users",
    navigation: {
      section: "management",
    },
    fields: [
      { name: "userId", description: "User ID" },
      { name: "tableName", description: "Table name" },
      { name: "field", description: "Field name" },
      { name: "action", description: "Action type" },
      { name: "oldData", description: "Previous data" },
      { name: "newData", description: "New data" },
      { name: "timestamp", description: "When the change occurred" },
    ],
  },
  "vehicle-brands": {
    name: "Vehicle Brands",
    url: "/dashboard/vehicles/brands",
    icon: TagIcon,
    description: "Vehicle brand management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Brand name" },
      { name: "description", description: "Brand description" },
      { name: "logo", description: "Brand logo" },
    ],
  },
  "vehicle-models": {
    name: "Vehicle Models",
    url: "/dashboard/vehicles/models",
    icon: Car,
    description: "Vehicle model management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Model name" },
      { name: "description", description: "Model description" },
      { name: "year", description: "Model year" },
      { name: "type", description: "Vehicle type" },
      { name: "image", description: "Model image" },
    ],
  },
  "company-fleets": {
    name: "Company Fleets",
    url: "/dashboard/vehicles/company-fleets",
    icon: Truck,
    description: "Company fleet management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Fleet name" },
      { name: "description", description: "Fleet description" },
      { name: "company_id", description: "Associated company" },
    ],
  },
  "user-fleets": {
    name: "User Fleets",
    url: "/dashboard/vehicles/user-fleets",
    icon: Users,
    description: "User fleet management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "name", description: "Fleet name" },
      { name: "description", description: "Fleet description" },
      { name: "user_id", description: "Associated user" },
    ],
  },
  "company-vehicles": {
    name: "Company Vehicles",
    url: "/dashboard/vehicles/company-vehicles",
    icon: BuildingIcon,
    description: "Company vehicle management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "company_id", description: "Associated company" },
      { name: "model_id", description: "Vehicle model" },
      { name: "fleet_id", description: "Associated fleet" },
      { name: "fleet_number", description: "Fleet number" },
      { name: "notes", description: "Vehicle notes" },
    ],
  },
  "user-vehicles": {
    name: "User Vehicles",
    url: "/dashboard/vehicles/user-vehicles",
    icon: Car,
    description: "User vehicle management",
    navigation: {
      section: "role",
      group: "vehicles",
    },
    fields: [
      { name: "user_id", description: "Associated user" },
      { name: "model_id", description: "Vehicle model" },
      { name: "fleet_id", description: "Associated fleet" },
      { name: "fleet_number", description: "Fleet number" },
      { name: "notes", description: "Vehicle notes" },
    ],
  },
  backups: {
    name: "Backups",
    url: "/dashboard/backups",
    icon: FileText,
    description: "Database backup management",
    navigation: {
      section: "management",
    },
    fields: [
      { name: "filename", description: "Backup filename" },
      { name: "created", description: "Backup date/time" },
      { name: "size", description: "Backup file size" },
    ],
  },
  "users-banned": {
    name: "Banned Users",
    url: "/dashboard/users/banned",
    icon: LockIcon,
    description: "Banned users management",
    navigation: {
      section: "role",
      group: "protection",
    },
    fields: [
      { name: "user_id", description: "Banned user" },
      { name: "banned_by", description: "Banned by user" },
      { name: "reason", description: "Ban reason" },
      { name: "reason_details", description: "Reason details" },
      { name: "notes", description: "Administrative notes" },
      { name: "is_permanent", description: "Permanent ban status" },
      { name: "banned_until", description: "Ban expiration date" },
      { name: "ip_address", description: "IP address when banned" },
      { name: "user_agent", description: "User agent when banned" },
      { name: "is_active", description: "Ban active status" },
      { name: "created", description: "Ban creation date" },
      { name: "updated", description: "Last updated date" },
    ],
  },
  "kill-switch": {
    name: "Kill Switch",
    url: "/dashboard/protection/kill-switch",
    icon: Power,
    description: "Site maintenance and access control",
    navigation: {
      section: "role",
      group: "protection",
    },
    fields: [
      { name: "is_active", description: "Kill switch status" },
      { name: "block_users", description: "Block regular users" },
      { name: "block_staff", description: "Block staff users" },
      { name: "maintenance_message", description: "Maintenance message" },
      { name: "reason", description: "Activation reason" },
      { name: "activated_by", description: "Activated by user" },
      { name: "activated_at", description: "Activation time" },
      { name: "deactivated_by", description: "Deactivated by user" },
      { name: "deactivated_at", description: "Deactivation time" },
    ],
  },
} as const;

export type Resource = keyof typeof resourcesConfig;
export const resources: Record<Resource, ResourceMetadata> = Object.entries(
  resourcesConfig
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: {
      ...value,
      fields: [...value.fields],
    },
  }),
  {} as Record<Resource, ResourceMetadata>
);

// Create defaultResources array for database initialization
export const defaultResources: ResourceData[] = Object.entries(
  resourcesConfig
).map(([key, value]) => ({
  id: uuidv4(),
  name: key,
  description: value.description,
  fields: value.fields.map((field) => ({
    id: uuidv4(),
    name: field.name,
    description: field.description || "",
    resource_id: uuidv4(), // This will be updated when inserted
    sensitive: false,
    created: new Date(),
    updated: new Date(),
  })),
  conditions: [],
  created: new Date(),
  updated: new Date(),
}));
